import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Reservation } from '@mews-commander/data-access';
import { ApplicationState, WithGroupId, WithReservationId } from '@mews-commander/types';
import { BulkCheckbox } from '@mews-commander/ui';
import { LocalizedText, useToggle } from '@mews/framework';
import { Accordion, Stack } from '@optimus-web/core';
import { setReservationsChecked } from '../../actions';
import { DateGroup, getCheckedReservationsSet, getReservationGroupNewStateGroups, StateGroup } from '../../selectors';
import { DateGroupLabel } from '../DateGroupLabel';
import ReservationListItem from '../reservationListItem';
import { ReservationStateName } from '../reservationListItem/ReservationStateName';
import { SelectedCount } from './BulkActionButtons/SelectedCount';
import { Offset } from './styles';

const SCROLL_DISTANCE = 80;

interface WithCheckedReservationIds {
    checkedReservationIds: Set<string>;
}

const getCheckedReservationIdsCount = (reservationIds: string[], checkedReservationIds: Set<string>) =>
    pipe(
        reservationIds,
        array.filter((id) => checkedReservationIds.has(id)),
        array.size
    );

const renderReservationInterval = ({ startUtc, endUtc }: Reservation, textKey: Mews.TextKey | undefined) => (
    <span>
        {textKey ? <LocalizedText textKey={textKey} /> : <DateGroupLabel startUtc={startUtc} endUtc={endUtc} />}
    </span>
);
interface StateGroupsProps extends WithGroupId, WithReservationId {
    scrollElementRef: RefObject<HTMLDivElement>;
}
export const StateGroups = ({ groupId, reservationId, scrollElementRef }: StateGroupsProps) => {
    const stateGroups = useSelector((state: ApplicationState) => getReservationGroupNewStateGroups(state, { groupId }));
    const checkedReservationIds = useSelector((state: ApplicationState) =>
        getCheckedReservationsSet(state, { scope: groupId })
    );
    useEffect(() => {
        const selectedReservationElement = document.querySelector(
            `[data-test-reservation-accordion='${reservationId}']`
        );
        const scrollElement = scrollElementRef.current;

        if (selectedReservationElement && scrollElement) {
            const baseScrollOffset =
                selectedReservationElement.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top;
            const baseScrollTop = scrollElement.scrollTop + baseScrollOffset;
            const scrollDistance = Math.min(baseScrollTop, SCROLL_DISTANCE);

            scrollElement.scrollTop = baseScrollTop - scrollDistance;
            scrollElement.scrollBy({ top: scrollDistance, behavior: 'smooth' });
        }
    }, [reservationId, groupId, scrollElementRef]);

    return (
        <>
            {stateGroups.map((stateGroup) => (
                <StateGroupEntries
                    stateGroup={stateGroup}
                    checkedReservationIds={checkedReservationIds}
                    groupId={groupId}
                    key={stateGroup.textKey}
                    reservationId={reservationId}
                />
            ))}
        </>
    );
};

interface StateGroupProps extends WithGroupId, WithReservationId, WithCheckedReservationIds {
    stateGroup: StateGroup;
}
const StateGroupEntries = ({
    stateGroup: { dateGroups },
    groupId,
    checkedReservationIds,
    reservationId,
}: StateGroupProps) => {
    const reservationIds = dateGroups.flatMap(({ reservations }) => reservations.map(({ id }) => id));
    const totalStateGroupReservationCount = reservationIds.length;

    const checkedStateGroupReservationCount = getCheckedReservationIdsCount(reservationIds, checkedReservationIds);
    const isAnyChecked = checkedStateGroupReservationCount > 0;
    const areAllChecked = checkedStateGroupReservationCount === totalStateGroupReservationCount;

    const [expanded, toggleExpand] = useToggle({
        initialValue: dateGroups.some(({ reservations }) => reservations.some(({ id }) => id === reservationId)),
    });

    const dispatch = useDispatch();
    const handleChange = () =>
        dispatch(
            setReservationsChecked({
                reservationIds,
                scope: groupId,
                isChecked: !areAllChecked,
            })
        );
    const firstDateGroup = dateGroups[0];
    const firstReservation = firstDateGroup.reservations[0];
    const { reservationTimeState, serviceOrderState, options } = firstReservation;

    const isSingleIntervalStateGroup = dateGroups.length === 1;

    return (
        <Accordion
            bodyPadding="0"
            withBodyBorder={false}
            expanded={expanded}
            onClick={toggleExpand}
            startSlot={<BulkCheckbox someSelected={isAnyChecked} allSelected={areAllChecked} onChange={handleChange} />}
            title={
                <Stack spacing="100" itemAlignment="center">
                    <ReservationStateName
                        reservationTimeState={reservationTimeState}
                        serviceOrderState={serviceOrderState}
                        options={options}
                    />
                    {isSingleIntervalStateGroup && renderReservationInterval(firstReservation, firstDateGroup.textKey)}
                </Stack>
            }
            endSlot={
                <SelectedCount
                    selectedCount={checkedStateGroupReservationCount}
                    totalCount={totalStateGroupReservationCount}
                />
            }
            body={
                <>
                    {dateGroups.map((dateGroup) => (
                        <DateGroupEntries
                            dateGroup={dateGroup}
                            checkedReservationIds={checkedReservationIds}
                            groupId={groupId}
                            reservationId={reservationId}
                            key={`${dateGroup.intervalStartUtc}-${dateGroup.intervalEndUtc}`}
                            isSingleIntervalStateGroup={isSingleIntervalStateGroup}
                        />
                    ))}
                </>
            }
        />
    );
};

interface DateGroupListProps extends WithGroupId, WithReservationId, WithCheckedReservationIds {
    dateGroup: DateGroup;
    isSingleIntervalStateGroup: boolean;
}

const DateGroupEntries = ({
    dateGroup: { reservations, textKey },
    groupId,
    checkedReservationIds,
    isSingleIntervalStateGroup,
    reservationId,
}: DateGroupListProps) => {
    const reservationIds = reservations.map(({ id }) => id);
    const totalDateGroupReservationCount = reservationIds.length;

    const checkedDateGroupReservationsCount = getCheckedReservationIdsCount(reservationIds, checkedReservationIds);
    const isAnyChecked = checkedDateGroupReservationsCount > 0;
    const areAllChecked = checkedDateGroupReservationsCount === totalDateGroupReservationCount;

    const [expanded, toggleExpand] = useToggle({
        initialValue: reservations.some(({ id }) => id === reservationId),
    });

    const dispatch = useDispatch();
    const handleChange = () =>
        dispatch(
            setReservationsChecked({
                reservationIds,
                scope: groupId,
                isChecked: !areAllChecked,
            })
        );

    const offset = isSingleIntervalStateGroup ? 0 : 1;

    const mapReservations = array.map(({ id }: Reservation) => (
        <ReservationListItem reservationId={id} scope={groupId} key={id} offset={offset + 1} />
    ));

    if (isSingleIntervalStateGroup) {
        return <>{mapReservations(reservations)}</>;
    }
    return (
        <Accordion
            bodyPadding="0"
            withBodyBorder={false}
            expanded={expanded}
            onClick={toggleExpand}
            startSlot={
                <Offset offset={offset}>
                    <BulkCheckbox someSelected={isAnyChecked} allSelected={areAllChecked} onChange={handleChange} />
                </Offset>
            }
            title={renderReservationInterval(reservations[0], textKey)}
            endSlot={
                <SelectedCount
                    selectedCount={checkedDateGroupReservationsCount}
                    totalCount={totalDateGroupReservationCount}
                />
            }
            body={<>{mapReservations(reservations)}</>}
        />
    );
};
